import Lenis from '@theme/lenis';
import { getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Smooth scrolling.
 *
 * Lenis scrolls `window` by default, but in the squeeze layout (>=990px)
 * `.page-wrapper` is the scroll container instead. The instance therefore has
 * to be pointed at whichever element is currently scrolling, and rebuilt when
 * that swaps at the breakpoint.
 *
 * Lenis writes scroll positions with `behavior: 'instant'` and syncs itself
 * from native scroll events, so the theme's own `scrollTo` helpers and the
 * `scroll-behavior: smooth` already set on `.page-wrapper` keep working.
 * Reduced-motion is handled inside Lenis via `respectReducedMotion`.
 */

/** @type {Lenis | null} */
let lenis = null;

/** @type {ResizeObserver | null} */
let contentObserver = null;

function create() {
  const container = getScrollContainer();
  const scrollsWindow = container === document.scrollingElement || container === document.documentElement;

  lenis = new Lenis({
    // `content` is the wrapper itself: `.page-wrapper` holds the header group,
    // main and footer as siblings, so there is no single element wrapping the
    // scrolled content. `scrollHeight` on the wrapper measures the same thing.
    ...(scrollsWindow ? {} : { wrapper: container, content: container }),
    autoRaf: true,
    // Let drawers, dropdowns and other scrollable panels scroll natively.
    allowNestedScroll: true,
  });

  // Because `content` is the wrapper, Lenis's own observer only ever sees that
  // element's fixed 100dvh box, so content growing or shrinking never triggers
  // a remeasure. Watch the children that actually change height instead.
  if (!scrollsWindow && 'ResizeObserver' in window) {
    contentObserver = new ResizeObserver(() => lenis?.resize());
    for (const child of container.children) {
      if (child instanceof HTMLElement) contentObserver.observe(child);
    }
  }
}

function destroy() {
  contentObserver?.disconnect();
  contentObserver = null;
  lenis?.destroy();
  lenis = null;
}

function refresh() {
  destroy();
  create();
}

/**
 * The live Lenis instance, or null before it starts. Read it per use rather
 * than caching — crossing the breakpoint replaces the instance.
 *
 * @returns {Lenis | null}
 */
export function getLenis() {
  return lenis;
}

create();
scrollContainerMediaQuery.addEventListener('change', refresh);
